/** UI-only Select value: follow next section in form order. */
export const GO_TO_DEFAULT_SENTINEL = "__next_section__";

export function normalizeSectionGoTo(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

export function usesDefaultNextSectionGoTo(section) {
  return section?.type !== "decision";
}

/** Attribute name of the next section in structure order, or "" if last. */
export function getDefaultNextSectionGoTo(sections, sectionId) {
  const index = sections.findIndex((s) => s.id === sectionId);
  if (index < 0) return "";
  const next = sections[index + 1];
  return next ? normalizeSectionGoTo(next.attributeName) : "";
}

export function isSectionGoToManual(section, sections) {
  if (section.goToManual === true) return true;
  if (section.goToManual === false) return false;
  if (!usesDefaultNextSectionGoTo(section)) return true;
  const defaultGoTo = getDefaultNextSectionGoTo(sections, section.id);
  const current = normalizeSectionGoTo(section.goTo);
  if (!current) return false;
  return current !== defaultGoTo;
}

export function getSectionGoToSelectValue(section, sections) {
  if (!usesDefaultNextSectionGoTo(section)) {
    return normalizeSectionGoTo(section.goTo);
  }
  if (!isSectionGoToManual(section, sections)) {
    return GO_TO_DEFAULT_SENTINEL;
  }
  return normalizeSectionGoTo(section.goTo);
}

/** Sections available as goTo targets (excludes the section being edited). */
export function getSectionGoToTargets(sections, excludeSectionId) {
  return sections
    .filter((s) => s.id !== excludeSectionId)
    .map((s) => ({
      value: (s.attributeName || "").trim(),
      label: (s.title || s.attributeName || "").trim() || "(untitled)",
    }))
    .filter((o) => o.value);
}

/**
 * Apply linear default goTo (next section) for non-decision sections unless overridden.
 */
export function applySectionNavigationDefaults(sections) {
  return sections.map((section, index) => {
    const nextSection = sections[index + 1];
    if (section.type === "decision") {
      return { ...section, goTo: section.goTo ?? null };
    }
    if (isSectionGoToManual(section, sections)) {
      return section;
    }
    return {
      ...section,
      goToManual: false,
      goTo: nextSection ? normalizeSectionGoTo(nextSection.attributeName) : "",
    };
  });
}

/** Published formJSON goTo for a section. */
export function resolveSectionGoToForExport(section, sections, sectionIndex) {
  if (section.type === "decision") {
    const v = normalizeSectionGoTo(section.goTo);
    return v || null;
  }
  if (!isSectionGoToManual(section, sections)) {
    const next = sections[sectionIndex + 1];
    const attr = next ? normalizeSectionGoTo(next.attributeName) : "";
    return attr || null;
  }
  const v = normalizeSectionGoTo(section.goTo);
  return v || null;
}

/** When a section attributeName changes, update goTo references on sections and decision options. */
export function remapGoToAttribute(sections, fields, oldAttr, newAttr) {
  if (!oldAttr || !newAttr || oldAttr === newAttr) {
    return { sections, fields };
  }
  const updatedSections = sections.map((s) =>
    String(s.goTo || "").trim() === oldAttr ? { ...s, goTo: newAttr } : s,
  );
  const updatedFields = fields.map((f) => {
    const options = f.metadata?.options;
    if (!Array.isArray(options)) return f;
    let changed = false;
    const nextOptions = options.map((opt) => {
      if (String(opt.goTo || "").trim() !== oldAttr) return opt;
      changed = true;
      return { ...opt, goTo: newAttr };
    });
    return changed ? { ...f, metadata: { ...f.metadata, options: nextOptions } } : f;
  });
  return { sections: updatedSections, fields: updatedFields };
}

/**
 * Infer client `goTo` / `goToManual` from published section JSON.
 * `goToManual` is not published; `goTo: null` on a non-last section means
 * manual "None" (skip remaining sections), not default next.
 */
export function parseImportedSectionGoTo(section, sectionIndex = -1, allSections = []) {
  const goTo = section.goTo;
  const type = section.type || "data";

  let result;
  if (type === "decision") {
    const trimmed = goTo != null ? normalizeSectionGoTo(goTo) : "";
    result = { goTo: trimmed || null, goToManual: true };
  } else if (goTo === null) {
    const isLast =
      Array.isArray(allSections) &&
      sectionIndex >= 0 &&
      sectionIndex === allSections.length - 1;
    result = { goTo: null, goToManual: !isLast };
  } else {
    const trimmed = goTo != null ? normalizeSectionGoTo(goTo) : "";
    if (trimmed === "") {
      result = { goTo: null, goToManual: false };
    } else {
      const defaultNext =
        Array.isArray(allSections) && sectionIndex >= 0 && allSections[sectionIndex + 1]
          ? normalizeSectionGoTo(allSections[sectionIndex + 1].attributeName)
          : "";
      result = {
        goTo: trimmed,
        goToManual: trimmed !== defaultNext,
      };
    }
  }
  return result;
}
